import CalculatePriceDetailWithIcon from "comps/CalculatePriceDetailWithIcon"
import MallCell from "comps/MallCell"
import MallSelectCell from "comps/MallSelectCell"
import PlaceholderImage from "comps/PlaceholderImage"
import SKuInventory from "comps/SkuInventory"
import Stepper from "comps/Stepper"
import useUrl from "hooks/useUrl"
import IconDel from "icons/icon-delete.svg"
import { numberAdd, numberDivide, numberMutiply } from "libs/compute"
import { MAX_QUANTITY } from "libs/cons"
import { NetPriceSourceEnum } from "libs/enums"
import { formatRenderPrice } from "libs/utils"
import { throttle } from "rambdax"
import { Toast } from "react-vant"
import { cls } from "reactutils"

type MmuSkuItemProps = {
  mmuSku: MmuMallSku
  showUsePart: Boolean
  selectTradeAgreement: TradeAgreementInfo
  onChange: (arr: Array<{ key; value }>) => void
  onDel: () => void
}
export default function MmuSkuItem({
  showUsePart,
  mmuSku,
  selectTradeAgreement,
  onChange,
  onDel,
}: MmuSkuItemProps) {
  const attrList = [
    ...(mmuSku.attributeList || []),
    ...(mmuSku.mmuAttributeList || []),
  ]
  const canEditPrice =
    mmuSku?.priceWithTaxHasInput || mmuSku?.priceWithOutTaxHasInput
  const canSale = mmuSku.agreementCanSale && mmuSku.areaCanSale
  let canNotSaleTip = !canSale
    ? !mmuSku.agreementCanSale
      ? "该商品不在规则范围内"
      : "该商品不在此地区范围内"
    : ""
  const { getPublicUrl } = useUrl()

  function getAttrValue(attribute: SkuDetail.SkuCategoryAttributeDTO) {
    let returnValue
    if (!attribute.hasCustomize) {
      const arr = attribute.categoryAttributeValueList
        .filter((categoryAttribute) => categoryAttribute.active)
        .map((categoryAttribute) => categoryAttribute.attributeValue)
      returnValue = arr.join("、")
    } else {
      const categoryAttributeValueItem = attribute.categoryAttributeValueList[0]
      returnValue =
        categoryAttributeValueItem.prefix +
        categoryAttributeValueItem.attributeValue +
        categoryAttributeValueItem.suffix
    }
    return returnValue || "--"
  }

  function getCanEditPriceMsg() {
    const netPriceSource = mmuSku?.netPriceSource
    const purchaserCustomizePrice =
      mmuSku?.skuForPriceCalculate?.purchaserCustomizePrice
    const noUnitConversionRate =
      mmuSku?.skuForPriceCalculate?.noUnitConversionRate
    if (purchaserCustomizePrice) {
      return "由采购方自行填写价格，请手动输入"
    } else if (noUnitConversionRate) {
      return "无理论重量，无法计算价格，请手动输入"
    }
    if (netPriceSource === NetPriceSourceEnum.INFO) {
      return "获取不到信息价，请手动输入"
    } else if (
      netPriceSource === NetPriceSourceEnum.LANGE ||
      netPriceSource === NetPriceSourceEnum.WDGTW ||
      netPriceSource === NetPriceSourceEnum.SHYSJSW
    ) {
      return "获取不到网价，请手动输入"
    } else if (netPriceSource === NetPriceSourceEnum.BASE) {
      return "获取不到基价，请手动输入"
    } else {
      return
    }
  }

  function handleChangePrice(value) {
    const taxRate = +numberAdd(numberDivide(mmuSku.taxRate, 100), 1)
    if (mmuSku.priceWithTaxHasInput) {
      const priceWithOutTax = +numberDivide(value, taxRate)
      onChange([
        {
          key: "priceWithOutTax",
          value: priceWithOutTax,
        },
        {
          key: "priceWithTax",
          value,
        },
      ])
    } else {
      const priceWithTax = +numberMutiply(value, taxRate)
      onChange([
        {
          key: "priceWithOutTax",
          value,
        },
        {
          key: "priceWithTax",
          value: priceWithTax,
        },
      ])
    }
  }

  function getSkuModel() {
    return attrList?.reduce((acc, attr) => {
      if (attr.categoryAttributeValueList?.length) {
        return acc + ` ${attr.attributeName}：${getAttrValue(attr)}`
      } else {
        return acc
      }
    }, "")
  }

  return (
    <div className={cls("sku", !canSale && "disabled")}>
      <span className="delIcon">
        <IconDel onClick={onDel} />
      </span>
      <div className="skuInfo">
        <div className="skuImg">
          <PlaceholderImage
            width={56}
            height={56}
            src={getPublicUrl(mmuSku.mainImage)}
          />
        </div>
        <div className="skuContent">
          <p className="skuName">{mmuSku.name}</p>
          {canSale ? (
            <p className="skuAttr">
              {attrList
                ?.filter((attr) => attr?.categoryAttributeValueList?.length)
                ?.map((attr, index) => (
                  <span className="attr-item" key={index}>
                    {attr.attributeName}：{getAttrValue(attr)}
                  </span>
                ))}
            </p>
          ) : (
            <p className="can-not-sale-tip">{canNotSaleTip}</p>
          )}
          <SKuInventory
            inventory={mmuSku.inventory}
            unitName={mmuSku.unitName}
            showInventory={mmuSku.showInventory}
          />
          {mmuSku.upstreamSupplierName && (
            <div className="content-delivery">
              由
              <span className="content-delivery-name">
                {mmuSku.upstreamSupplierName}
              </span>
              配送
            </div>
          )}
          {canSale ? (
            <>
              <div className="skuPriceAndQuantity">
                <div className="skuPrice">
                  &yen;
                  {canEditPrice ? (
                    <div className="input-price">
                      <Stepper
                        value={
                          mmuSku.priceWithTaxHasInput
                            ? mmuSku.priceWithTax
                            : mmuSku.priceWithOutTax
                        }
                        inputWidth="64px"
                        min={0}
                        step={0}
                        integer={false}
                        showAdd={false}
                        showMinus={false}
                        decimalLength={8}
                        placeholder="请手动输入网价"
                        onChange={(value) => {
                          handleChangePrice(value)
                        }}
                      />
                    </div>
                  ) : (
                    <span className="price">
                      {formatRenderPrice(mmuSku.priceWithTax)}
                    </span>
                  )}
                  /{mmuSku.unitName}
                  <div className="calc">
                    {!canEditPrice && mmuSku.skuForPriceCalculate ? (
                      <CalculatePriceDetailWithIcon
                        skuSysNo={mmuSku?.sysNo}
                        tradeAgreementSysNo={selectTradeAgreement?.sysNo}
                        skuName={mmuSku?.name}
                        skuMainImg={mmuSku?.mainImage}
                        skuModel={getSkuModel()}
                        skuForPriceCalculate={mmuSku.skuForPriceCalculate}
                      />
                    ) : null}
                  </div>
                </div>
                {canSale && (
                  <div className="calcStepper">
                    <Stepper
                      value={mmuSku.shopQuantity}
                      defaultValue={1}
                      min={0}
                      step={mmuSku.buyStep}
                      max={
                        mmuSku.inventory === null ||
                        mmuSku.inventory === undefined
                          ? MAX_QUANTITY
                          : mmuSku.inventory
                      }
                      inputWidth="52px"
                      integer={false}
                      decimalLength={4}
                      onChange={throttle((value) => {
                        if (value <= 0) {
                          onChange([{ key: "shopQuantity", value }])
                          onDel()
                        } else {
                          onChange([{ key: "shopQuantity", value }])
                        }
                      }, 300)}
                      onExceed={() => {
                        if (!!mmuSku.inventory) {
                          Toast.info(
                            `${mmuSku.name}库存量为${mmuSku.inventory}，请勿超量购买`,
                          )
                        }
                      }}
                      autoFix
                    />
                  </div>
                )}
              </div>
              {canEditPrice &&
                !(mmuSku.priceWithTaxHasInput
                  ? mmuSku.priceWithTax
                  : mmuSku.priceWithOutTax) && (
                  <p className="errorInfo">{getCanEditPriceMsg()}</p>
                )}
            </>
          ) : null}
        </div>
      </div>
      {canSale ? (
        <>
          {showUsePart && false && ( // 2024.7.31 刘伊扬，采购清单中不展示使用部位了
            <MallCell
              title="使用部位"
              value={mmuSku.usePart}
              placeholder="请填写使用部位"
              usePopupInput
              popupInputTitle="使用部位"
              popupInputPlaceholder="请填写使用部位；不超过200个文字。"
              popupInputConfirm={(value) => {
                onChange([{ key: "usePart", value }])
              }}
              isLink
            />
          )}
          {/* 2025 4月零星 21 商品详情不展示使用分包 */}
          {/* <MallSelectCell
            title="使用分包"
            value={mmuSku.subContractorName ? {name: mmuSku.subContractorName, sysNo: mmuSku.sysNo} : null}
            placeholder="请选择使用分包"
            usePopupInput
            popupInputTitle="使用分包"
            popupInputPlaceholder="请选择使用分包。"
            popupInputConfirm={(obj: MmuMallSku) => {
              onChange([{ key: "subContractorName", value: obj.name }, {key: "subContractorSysNo", value: obj.sysNo}])
            }}
            isLink
          /> */}
          <MallCell
            title="浇筑部位"
            value={mmuSku.pourLocation}
            placeholder="请填写浇筑部位"
            usePopupInput
            popupInputTitle="浇筑部位"
            popupInputPlaceholder="请填写使用部位；不超过200个文字。"
            popupInputConfirm={(value) => {
              onChange([{ key: "pourLocation", value }])
            }}
            isLink
          />
          <MallCell
            title="备注"
            value={mmuSku.comment}
            placeholder="请填写商品明细备注"
            usePopupInput
            popupInputTitle="备注"
            popupInputPlaceholder="选填，请填写商品明细备注；不超过200个文字。"
            popupInputConfirm={(value) => {
              onChange([{ key: "comment", value }])
            }}
            isLink
          />
        </>
      ) : null}

      <style jsx>{`
        .sku {
          padding-top: 17px;
          background-color: #fff;
          position: relative;
        }
        .sku:after {
          content: "";
          position: absolute;
          width: 100%;
          height: 1px;
          background-color: #eeeeee;
          z-index: 1;
          bottom: 0;
        }
        .delIcon {
          position: absolute;
          display: block;
          width: 14px;
          height: 14px;
          top: 16px;
          right: 16px;
        }
        .skuInfo {
          padding: 0 16px;
          display: flex;
          flex-direction: row;
        }
        .skuImg {
          width: 56px;
          height: 56px;
          flex: 0 0 auto;
        }
        .skuContent {
          flex: 1 1 auto;
          padding-left: 16px;
        }
        .skuName {
          font-size: 14px;
          color: #000000;
          line-height: 14px;
          margin-bottom: 8px;
          padding-right: 20px;
        }
        .skuAttr {
          font-size: 12px;
          color: #666666;
          line-height: 18px;
          margin-bottom: 12px;
        }

        .attr-item {
          display: inline-block;
          vertical-align: middle;
          margin-right: 10px;
        }

        .attr-item:last-child {
          margin-right: 0;
        }

        .skuPriceAndQuantity {
          height: 22px;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          margin-top: 9px;
        }
        .calcStepper {
          flex: 0 0 auto;
          display: flex;
          flex-direction: row;
        }
        .calc {
          margin-right: 5px;
          display: flex;
          align-items: center;
          margin-left: 10px;
        }
        .skuPrice {
          height: 22px;
          font-size: 12px;
          color: #ea5604;
          line-height: 13px;
          display: flex;
          flex-direction: row;
          align-items: center;
        }
        .price {
          font-weight: 500;
          margin-left: 2px;
        }
        .input-price {
          margin: 0 3px;
        }
        .input-price :global(.stepper-input) {
          text-align: left;
          color: #ea5604;
        }
        .errorInfo {
          font-size: 11px;
          color: #ea5604;
          line-height: 12px;
          margin-top: 5px;
          padding-right: 10px;
          margin-bottom: 0px;
        }
        .sku.disabled {
          padding-bottom: 20px;
        }
        .sku.disabled .skuName {
          color: #999999;
        }
        .can-not-sale-tip {
          font-size: 12px;
          color: #ea5604;
          margin-top: 10px;
        }
        .content-delivery {
          display: flex;
          padding-top: 3px;
          color: #666;
          font-size: 11px;
          white-space: nowrap;
        }
        .content-delivery-name {
          padding: 0 5px;
          color: #999;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      `}</style>
    </div>
  )
}
